import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './ShopLogin.css';
import { toast } from "react-toastify";
import { registerfunction } from '../../services/Apis';
import { BACKEND_URL } from '../../services/Helper';
import defaultimg from '../../Assets/hero.jpg';

const notifyB = (msg) => toast.success(msg);
const notifyA = (msg) => toast.error(msg);

const ShopLogin = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);

  const [formData, setFormData] = useState({
    ownerName: '',
    shopName: '',
    contactNumber: '',
    email: '',
    category: '',
    address: '',
    latitude: '',
    longitude: ''
  });
  const [homeDelivery, setHomeDelivery] = useState(false);

  const [mallId, setMallId] = useState('');
  const [malls, setMalls] = useState([]);
  const [floor, setFloor] = useState('');
  const [shopUnit, setShopUnit] = useState('');
  const [shopImage, setShopImage] = useState(null);
  const [preview, setPreview] = useState(defaultimg);
  const [loadingLocation, setLoadingLocation] = useState(false);
  const [loadingReg, setLoadingReg] = useState(false);
  const [formSuccess, setFormSuccess] = useState(false);

  useEffect(() => {
    fetch(`${BACKEND_URL}/nearbe/getallmalls`)
      .then(r => r.json())
      .then(d => setMalls(d.malls || []))
      .catch(() => {});
  }, []);

  const uploadImageToCloudinary = async (imageBlob) => {
    const data = new FormData();
    data.append("file", imageBlob);
    data.append("upload_preset", process.env.REACT_APP_CLOUDINARY_PRESET);
    data.append("cloud_name", process.env.REACT_APP_CLOUDINARY_CLOUD);

    const response = await fetch(`https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUDINARY_CLOUD}/image/upload`, {
      method: "POST",
      body: data,
    });

    if (!response.ok) throw new Error("Cloudinary upload failed");
    return response.json();
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const getLocation = () => {
    if (navigator.geolocation) {
      setLoadingLocation(true);
      setFormData((prev) => ({ ...prev, address: "⏳ Getting location..." }));

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          try {
            const response = await fetch(
              `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`
            );
            const data = await response.json();
            setFormData((prev) => ({
              ...prev,
              address: data.display_name || `${latitude}, ${longitude}`,
              latitude,
              longitude,
            }));
            notifyB("Location fetched successfully");
          } catch (error) {
            notifyA("Unable to fetch location details");
          } finally {
            setLoadingLocation(false);
          }
        },
        (error) => {
          notifyA("Location access denied or unavailable");
          setLoadingLocation(false);
        }
      );
    } else {
      notifyA("Geolocation not supported by this browser");
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setShopImage(file);
      setPreview(URL.createObjectURL(file)); // Live preview
    }
  };

  const handleNext = () => {
    const { ownerName, shopName, contactNumber, email, category, address, latitude, longitude } = formData;
    const phoneRegex = /^[0-9]{10}$/;

    if (
      !ownerName ||
      !shopName ||
      !contactNumber ||
      !email ||
      !category ||
      !address ||
      latitude === "" ||
      longitude === ""
    ) {
      notifyA("Please fill all the fields properly");
    } else if (!phoneRegex.test(contactNumber)) {
      notifyA("Please enter a valid 10-digit contact number");
    } else if (!email.includes("@")) {
      notifyA("Please enter a valid email address");
    } else {
      setStep(2);
    }
  };

  const handleBack = () => setStep(1);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!shopImage) {
      notifyA("Please upload a shop photo!");
      return;
    }

    try {
      setLoadingReg(true);

      //Upload image to Cloudinary
      const uploadRes = await uploadImageToCloudinary(shopImage);
      const imageUrl = uploadRes.secure_url;
      console.log(imageUrl);


      // Prepare payload
      const selectedMall = mallId ? malls.find(m => m._id === mallId) : null;
      const payload = {
        ...formData,
        latitude: Number(formData.latitude),
        longitude: Number(formData.longitude),
        shopImage: imageUrl,
        homeDelivery,
        ...(mallId ? {
          mall: mallId,
          mallName: selectedMall?.mallName || null,
          floor: floor || null,
          shopUnit: shopUnit || null,
        } : {}),
      };

      // Call backend
      const response = await registerfunction(payload);

      if (response.status === 201) {
        notifyB("Shop registered successfully!");
        setFormData({
          ownerName: '',
          shopName: '',
          contactNumber: '',
          email: '',
          category: '',
          address: '',
          latitude: '',
          longitude: ''
        });
        setMallId('');
        setFloor('');
        setShopUnit('');
        setHomeDelivery(false);
        setShopImage(null);
        setPreview("/default-shop.jpg");
        setStep(1);
        navigate("/shoplogin");
      } else {
        notifyA(response.error || "Failed to register shop");
      }
    } catch (err) {
      console.error(err);
      notifyA("Something went wrong while registering");
    } finally {
      setLoadingReg(false);
    }
  };

  return (
    <div className="shoplogin-container">
      <form
        className={`shoplogin-form ${formSuccess ? "success" : ""}`}
        onSubmit={handleSubmit}
      >
        <h2>Register Shop</h2>

        {step === 1 ? (
          <>
            <div className="form-row">
              <input
                type="text"
                name="ownerName"
                placeholder="Shop Owner Name"
                value={formData.ownerName}
                onChange={handleChange}
                required
              />
              <input
                type="text"
                name="shopName"
                placeholder="Shop Name"
                value={formData.shopName}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-row">
              <input
                type="tel"
                name="contactNumber"
                placeholder="Contact Number"
                value={formData.contactNumber}
                onChange={handleChange}
                required
              />
              <input
                type="email"
                name="email"
                placeholder="Email Address"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-row">
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                required
              >
                <option value="">Select Category</option>
                <option value="Cafe">Cafe</option>
                <option value="Restaurant">Restaurant</option>
                <option value="Saloon">Saloon</option>
                <option value="Fruits">Fruits & Vegetables</option>
                <option value="Electronics">Electronics</option>
                <option value="Hardware">Hardware</option>
                <option value="Car">Car & Bike</option>
                <option value="Autoparts">Autoparts</option>
                <option value="Medical">Medical</option>
                <option value="Hospitals">Hospitals</option>
                <option value="Gym">Gym</option>
                <option value="Individual">Individual Services(Pandit, Advocates, CAs)</option>
                <option value="Entertainment">Entertainment</option>
                <option value="Footwear">Footwear</option>
                <option value="Textiles">Textiles</option>
                <option value="Others">Others</option>
              </select>
            </div>

            <div className="form-row address-row">
              <textarea
                name="address"
                placeholder="Shop Address"
                value={formData.address}
                onChange={handleChange}
                rows="3"
                required
              ></textarea>
              <button
                type="button"
                className="location-btn"
                onClick={getLocation}
                disabled={loadingLocation}
              >
                {loadingLocation ? "⏳ Getting location..." : "📍 Get My Location"}
              </button>
            </div>

            {malls.length > 0 && (
              <div className="form-row mall-section">
                <select value={mallId} onChange={e => { setMallId(e.target.value); setFloor(''); setShopUnit(''); }}>
                  <option value="">🏬 Shop inside a mall? (optional)</option>
                  {malls.map(m => (
                    <option key={m._id} value={m._id}>{m.mallName} — {m.mallCity}</option>
                  ))}
                </select>
                {mallId && (
                  <div className="mall-location-fields">
                    <input
                      type="text"
                      placeholder="Floor (e.g. Ground Floor, 1st Floor)"
                      value={floor}
                      onChange={e => setFloor(e.target.value)}
                      className="mall-location-input"
                    />
                    <input
                      type="text"
                      placeholder="Unit / Shop No. (e.g. G-12, Shop 4)"
                      value={shopUnit}
                      onChange={e => setShopUnit(e.target.value)}
                      className="mall-location-input"
                    />
                  </div>
                )}
              </div>
            )}

            <div className="form-row delivery-toggle-row">
              <label className="delivery-toggle-label">
                <input
                  type="checkbox"
                  checked={homeDelivery}
                  onChange={(e) => setHomeDelivery(e.target.checked)}
                  className="delivery-toggle-checkbox"
                />
                <span className="delivery-toggle-icon">🚚</span>
                <span>We offer <strong>Free Home Delivery</strong></span>
              </label>
            </div>

            <div className="form-row buttons">
              <button type="button" className="submit-btn" onClick={handleNext}>
                Next
              </button>
              <button
                type="button"
                className="back-btn"
                onClick={() => navigate("/")}
              >
                Go Back
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="image-preview">
              <img src={preview} alt="Shop Preview" />
            </div>

            {/* Hidden File Input */}
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              id="shopImageInput"
              style={{ display: "none" }}
            />

            {/* Custom Button to trigger file input */}
            <button
              type="button"
              className="addshop-btn"
              onClick={() => document.getElementById("shopImageInput").click()}
            >
              Add Shop Image
            </button>

            <div className="form-row buttons">
              <button type="submit" className="submit-btn" disabled={loadingReg}>
                {loadingReg ? "⏳ Registering..." : "Register"}
              </button>

              <button type="button" className="back-btn" onClick={handleBack}>
               Go Back
              </button>
            </div>
          </>
        )}
      </form>
    </div>
  );
};

export default ShopLogin;
